import type { FutsalMatchState, FutsalAction } from './futsal-types';

// ─── Pure action reducers ─────────────────────────────────────────────────────

/**
 * Record a goal for the given team.
 * isCaptainGoal is stored only when true (falsy → undefined).
 */
export function recordGoal(
  state: FutsalMatchState,
  team: 'home' | 'away',
  isCaptainGoal: boolean,
  actionId: string,
): FutsalMatchState {
  const action: FutsalAction = {
    id: actionId,
    type: 'goal',
    team,
    period: state.currentPeriod,
    timestamp: new Date().toISOString(),
    timerValue: state.timerSeconds,
    isCaptainGoal: isCaptainGoal || undefined,
  };
  return {
    ...state,
    homeScore: team === 'home' ? state.homeScore + 1 : state.homeScore,
    awayScore: team === 'away' ? state.awayScore + 1 : state.awayScore,
    actions: [...state.actions, action],
  };
}

/**
 * Record a (accumulated) foul for the given team.
 * Returns the updated state AND whether the foul threshold has now been reached.
 */
export function recordFoul(
  state: FutsalMatchState,
  team: 'home' | 'away',
  actionId: string,
): { state: FutsalMatchState; thresholdReached: boolean } {
  const currentFouls = team === 'home' ? state.homeFouls : state.awayFouls;
  const newFouls = currentFouls + 1;
  const thresholdReached = newFouls >= state.config.foulThreshold;

  const action: FutsalAction = {
    id: actionId,
    type: 'foul',
    team,
    period: state.currentPeriod,
    timestamp: new Date().toISOString(),
    timerValue: state.timerSeconds,
  };

  const nextState: FutsalMatchState = {
    ...state,
    homeFouls: team === 'home' ? newFouls : state.homeFouls,
    awayFouls: team === 'away' ? newFouls : state.awayFouls,
    actions: [...state.actions, action],
  };

  return { state: nextState, thresholdReached };
}

/**
 * Record the outcome of a penalty kick.
 * - penalty_goal: increments the scoring team's score.
 * - Any type: resets the fouling team's foul accumulator to 0.
 */
export function recordPenaltyOutcome(
  state: FutsalMatchState,
  params: {
    type: 'penalty_goal' | 'penalty_saved' | 'penalty_missed';
    team: 'home' | 'away';
    isCaptainGoal?: boolean;
    foulingTeam?: 'home' | 'away';
  },
  actionId: string,
): FutsalMatchState {
  const { type, team, isCaptainGoal, foulingTeam } = params;
  const action: FutsalAction = {
    id: actionId,
    type,
    team,
    period: state.currentPeriod,
    timestamp: new Date().toISOString(),
    timerValue: state.timerSeconds,
    isCaptainGoal: type === 'penalty_goal' ? isCaptainGoal || undefined : undefined,
  };
  const isGoal = type === 'penalty_goal';
  const resetHome = foulingTeam === 'home';
  const resetAway = foulingTeam === 'away';
  return {
    ...state,
    homeScore: isGoal && team === 'home' ? state.homeScore + 1 : state.homeScore,
    awayScore: isGoal && team === 'away' ? state.awayScore + 1 : state.awayScore,
    homeFouls: resetHome ? 0 : state.homeFouls,
    awayFouls: resetAway ? 0 : state.awayFouls,
    actions: [...state.actions, action],
  };
}

/**
 * Undo the last action in the current period.
 * Reverts score/foul counters for goal/penalty_goal/foul; no-ops for others.
 * Returns state unchanged if there are no current-period actions.
 */
export function undoLast(state: FutsalMatchState): FutsalMatchState {
  const currentPeriodActions = state.actions.filter((a) => a.period === state.currentPeriod);
  if (currentPeriodActions.length === 0) return state;

  const lastAction = currentPeriodActions[currentPeriodActions.length - 1];
  const newActions = state.actions.filter((a) => a.id !== lastAction.id);

  let homeScore = state.homeScore;
  let awayScore = state.awayScore;
  let homeFouls = state.homeFouls;
  let awayFouls = state.awayFouls;

  switch (lastAction.type) {
    case 'goal':
    case 'penalty_goal':
      if (lastAction.team === 'home') homeScore = Math.max(0, homeScore - 1);
      else awayScore = Math.max(0, awayScore - 1);
      break;
    case 'foul':
      if (lastAction.team === 'home') homeFouls = Math.max(0, homeFouls - 1);
      else awayFouls = Math.max(0, awayFouls - 1);
      break;
    // direct_foul / penalty_saved / penalty_missed / kick_off → no counter change
  }

  return {
    ...state,
    actions: newActions,
    homeScore,
    awayScore,
    homeFouls,
    awayFouls,
  };
}

/**
 * Mark the most recent goal or penalty_goal as a captain goal.
 * Returns state unchanged if no goal action exists.
 */
export function markLastGoalCaptain(state: FutsalMatchState): FutsalMatchState {
  const lastGoalIdx = [...state.actions]
    .reverse()
    .findIndex((a) => a.type === 'goal' || a.type === 'penalty_goal');
  if (lastGoalIdx === -1) return state;

  const actualIdx = state.actions.length - 1 - lastGoalIdx;
  const updatedActions = [...state.actions];
  updatedActions[actualIdx] = {
    ...updatedActions[actualIdx],
    isCaptainGoal: true,
  };
  return { ...state, actions: updatedActions };
}

// ─── Direct foul helpers ──────────────────────────────────────────────────────

/**
 * Record a direct foul: appends a `direct_foul` action for the fouling team.
 * Does NOT touch the per-team accumulator (a direct foul is not a step toward
 * the threshold). The opponent's penalty shot is opened by the UI; on its
 * resolution the existing penalty path resets the accumulator to 0.
 */
export function appendDirectFoul(
  state: FutsalMatchState,
  team: 'home' | 'away',
  actionId: string,
): FutsalMatchState {
  const action: FutsalAction = {
    id: actionId,
    type: 'direct_foul',
    team,
    period: state.currentPeriod,
    timestamp: new Date().toISOString(),
    timerValue: state.timerSeconds,
  };
  return { ...state, actions: [...state.actions, action] };
}

/** True for any action that counts as a foul for stats (normal or direct). */
export function isFoulAction(action: FutsalAction): boolean {
  return action.type === 'foul' || action.type === 'direct_foul';
}

/** Lifetime foul total for a team — counts accumulated fouls AND direct fouls. */
export function countLifetimeFouls(actions: FutsalAction[], team: 'home' | 'away'): number {
  return actions.filter((a) => a.team === team && isFoulAction(a)).length;
}
